// SYNC: duplicated verbatim across cres-t1/t4/t5/t6 (separate frontend apps, no shared package) —
// keep the copies in sync. The assistant backend is centralized in cres-aws-t2.
// Client helper for the ParkourAI in-app assistant. The assistant backend is CENTRALIZED in the
// Developer (t2) app — there is no per-app assistant Lambda. We call t2/v1/assistant cross-app via
// AxiosDeveloperAPI (t2's authorizer accepts our token; the platform DB is shared), so the assistant
// can answer both this app's questions and cross-app (e.g. Developer exception) questions.
//   POST {Message, Messages, App} -> {Reply, Messages, Activity, Model, App, Persona}
// The conversation is stateless server-side: the raw `messages` array is opaque (Anthropic/Bedrock
// message blocks) and is round-tripped back each turn to preserve context. The final Reply and the
// per-tool Activity steps both come back on this HTTP response (the source of truth).
//
// THE TURN CARRIES THE APP AND THE PERSONA. `App` picks the tool set and prompt the backend offers
// (this app is the QMS panel: QMS reads plus the Developer exception tools). The persona travels as
// the same `visibility-mode` / `visibility-value` headers every t1 request carries, read from the
// session the way AxiosRestAPI fills them in -- the assistant reads exactly what the user can read,
// and the backend verifies the persona is one the user holds before any tool runs (CC-PRSN-DENY).
// AxiosDeveloperAPI does not add those headers itself: nothing else in t2 is scoped by a persona.
import AxiosDeveloperAPI from "../util/AxiosDeveloperAPI";
import {SessionContext} from "../util/SessionContext";

/** Which app's panel this is. Each app's copy of this file names its own. */
export const ASSISTANT_APP = "QMS";

/** One tool step the assistant took this turn (for optional display). */
export type AssistantActivity = {
    kind: string;
    text: string;
};

/** A record a tool showed this turn, collected server-side from the tool results (never from the
 *  model's prose), with the route this app opens it at. */
export type AssistantReference = {
    kind: "Record" | "ChangeOrder" | "Documentation";
    objectId: string;
    name: string | null;
    type: string | null;
    usid: string;
    path: string;
};

/** The route for a reference. `/object/{usid}` names a REVISION, which is what the server sends. A
 *  Documentation reference's USID is already the guide's path (/docs/<page>#<section>). */
export function referencePath(kind: string, usid: string): string {
    if (kind === "Documentation") return usid;
    return kind === "ChangeOrder" ? `/change-orders/${usid}` : `/object/${usid}`;
}

/** A write the assistant drafted this turn: a pending proposal the user approves or rejects on a card. */
export type AssistantProposal = {
    usid: string;
    proposalType: string;
    title: string;
    summary: string;
    /** What approving it makes, so the card can open it: "Record" | "ChangeOrder"; absent for other types. */
    open?: string;
};

export type AssistantTurn = {
    /** The assistant's final text reply for this turn. */
    reply: string;
    /** Updated raw message history — pass this straight back on the next turn. */
    messages: any[];
    /** Tool steps taken this turn. */
    activity: AssistantActivity[];
    /** Friendly name of the model that answered (may differ from the default after a fallback). */
    model: string | null;
    /** The persona the turn ran under, as the backend verified it; null when none was active. */
    persona: {Mode: string; Value: string} | null;
    /** The records the tools showed this turn, in first-sighting order, capped by the server. */
    references: AssistantReference[];
    /** The writes drafted this turn, each a pending proposal. */
    proposals: AssistantProposal[];
};

/** The persona headers for one turn: the session's active visibility, when one is set. */
export function personaHeaders(): Record<string, string> {
    const active = new SessionContext().getActiveVisibility();
    if (active?.Mode && active?.Value) {
        return {"visibility-mode": active.Mode, "visibility-value": active.Value};
    }
    return {};
}

/** Send one user message and get the assistant's reply plus the updated history. */
export async function sendAssistantMessage(message: string, priorMessages: any[]): Promise<AssistantTurn> {
    const res = await AxiosDeveloperAPI({
        method: "POST",
        url: "t2/v1/assistant",
        headers: personaHeaders(),
        data: {Message: message, Messages: priorMessages, App: ASSISTANT_APP},
    });
    const body = res?.data ?? {};
    return {
        reply: String(body.Reply ?? ""),
        messages: Array.isArray(body.Messages) ? body.Messages : priorMessages,
        activity: Array.isArray(body.Activity) ? body.Activity : [],
        model: body.Model ? String(body.Model) : null,
        persona: body.Persona && body.Persona.Mode ? {Mode: String(body.Persona.Mode), Value: String(body.Persona.Value)} : null,
        proposals: (Array.isArray(body.Proposals) ? body.Proposals : [])
            .filter((p: any) => p && p.USID)
            .map((p: any): AssistantProposal => ({
                usid: String(p.USID),
                proposalType: String(p.ProposalType ?? ""),
                title: String(p.Title ?? ""),
                summary: String(p.Summary ?? ""),
                open: p.Open ? String(p.Open) : undefined,
            })),
        references: (Array.isArray(body.References) ? body.References : [])
            .filter((r: any) => r && r.ObjectID && r.USID)
            .map((r: any) => ({
                kind: r.Kind === "ChangeOrder" ? "ChangeOrder" : r.Kind === "Documentation" ? "Documentation" : "Record",
                objectId: String(r.ObjectID),
                name: r.Name ? String(r.Name) : null,
                type: r.Type ? String(r.Type) : null,
                usid: String(r.USID),
                path: referencePath(String(r.Kind), String(r.USID)),
            })),
    };
}


/** Open a guide section in the window the Documentation page uses, so one guide reuses one window. */
export function openGuide(path: string) {
    const name = "cresGuide:" + path.replace(/^\/docs\//, "").replace(/#.*$/, "").replace(/\W+/g, "_");
    window.open(path, name, "width=1200,height=900,scrollbars=yes,resizable=yes");
}

/** The event a screen raises to put a question to the assistant on the user's behalf. */
export const ASK_ASSISTANT_EVENT = "cres:assistant:ask";

/** Open the assistant drawer and send `message` as the user's turn -- the way a screen hands the
 *  conversation to the assistant WITH its context (which record, which field, what the screen
 *  found), so the user does not have to retype what the screen already knows. The drawer listens
 *  for the event wherever it is mounted. */
export function askAssistant(message: string) {
    window.dispatchEvent(new CustomEvent(ASK_ASSISTANT_EVENT, {detail: {message}}));
}

// ── the model the assistant answers with (2026-10-31) ──────────────────────────────────────────
// Ed, 2026-10-29: "Do not show anything about the model" -- and then, 2026-10-31, "allow a user to choose
// the model and disclose the cost": the choice reverses the earlier rule deliberately. The administrator
// bounds the list per account in Administration; the person picks within it here and sees the price.
export type AssistantModel = {Name: string; Label: string; Allowed: boolean; Default: boolean; Chosen: boolean; PerTurn: number | null; Priced: boolean};
export type AssistantModels = {Models: AssistantModel[]; Chosen: string; Default: string; Pick: string | null};

export async function getAssistantModels(): Promise<AssistantModels> {
    const res = await AxiosDeveloperAPI({method: "GET", url: "t2/v1/assistant_models"});
    return res.data as AssistantModels;
}

export async function setAssistantModel(name: string | null): Promise<AssistantModels> {
    const res = await AxiosDeveloperAPI({method: "PUT", url: "t2/v1/assistant_models", data: {Model: name ?? ""}});
    return res.data as AssistantModels;
}

/** "about 1.8¢ a turn", or null when the model carries no rate. */
export function perTurnLabel(perTurn: number | null): string | null {
    if (perTurn == null) return null;
    return perTurn < 0.01 ? `about ${(perTurn * 100).toFixed(2)}¢ a turn` : `about ${(perTurn * 100).toFixed(1)}¢ a turn`;
}
